import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingCart, Lock, Phone, MessageCircle } from "lucide-react";

const Detail = () => {
  const { id } = useParams(); // product ID from URL
  const navigate = useNavigate();

  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

  const [product, setProduct] = useState(null);
  const [seller, setSeller] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }

    const fetchProduct = async () => {
      try {
        const res = await axios.get(`https://api.kamulyaan.id/books/${id}`);
        setProduct(res.data);
        return res.data; // return for chaining
      } catch (err) {
        console.error("Failed to load product:", err);
        setError("Gagal memuat detail produk");
        return null;
      }
    };

    const fetchImages = async () => {
      try {
        // FIX: Gunakan endpoint yang sama seperti Update.jsx
        const res = await axios.get(`https://api.kamulyaan.id/addimage/${id}/images`);
        setImages(res.data.data || res.data || []);
      } catch (err) {
        console.error("Failed to load images:", err);
      }
    };

    const fetchSeller = async (storeId) => {
      if (!storeId) return;
      try {
        const res = await axios.get(`https://api.kamulyaan.id/seller/${storeId}`);
        if (res.data.success && res.data.seller) {
          setSeller(res.data.seller);
        } else {
          console.warn("Seller response missing 'seller' field", res.data);
        }
      } catch (err) {
        console.error("Failed to load seller info:", err);
      }
    };

    const loadAll = async () => {
      const prod = await fetchProduct();
      await Promise.all([
        fetchImages(),
        prod?.storeid ? fetchSeller(prod.storeid) : Promise.resolve()
      ]);
      setLoading(false);
    };

    loadAll();
  }, [id, isLoggedIn]);

  // NEW: Navigate to order page when "Beli Sekarang" is clicked
  const handleBuyNow = () => {
    navigate(`/order/${id}`);
  };

  if (!isLoggedIn) {
    return (
      <div style={{ 
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px", background: "#f9f9f9"
      }}>
        <div style={{ 
          textAlign: "center", maxWidth: "400px", padding: "32px",
          background: "white", borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)"
        }}>
          <Lock size={48} color="#e63946" style={{ marginBottom: "16px" }} />
          <h2 style={{ marginBottom: "12px", color: "#333" }}>Akses Dibatasi</h2>
          <p style={{ color: "#555", marginBottom: "24px" }}>
            Anda harus login untuk melihat detail produk ini.
          </p>
          <button
            onClick={() => navigate("/login")}
            style={{
              padding: "12px 32px", background: "#1976d2", color: "white",
              border: "none", borderRadius: "8px", fontSize: "1rem",
              fontWeight: "bold", cursor: "pointer",
            }}
          >
            Login Sekarang
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ 
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "1.2rem", color: "#666"
      }}>
        Memuat detail produk...
      </div>
    );
  }

  if (error || !product) {
    return (
      <div style={{ 
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", color: "#d32f2f", padding: "20px"
      }}>
        {error || "Produk tidak ditemukan"}
        <button
          onClick={() => navigate(-1)}
          style={{
            marginTop: "24px", padding: "10px 24px",
            background: "#1976d2", color: "white", border: "none",
            borderRadius: "8px", cursor: "pointer",
          }}
        >
          ← Kembali
        </button>
      </div>
    );
  }

  // FIX: Main image hanya dari product.cover
  const mainImage = product.cover || "https://placehold.co/600x400?text=Tanpa+Gambar";

  return (
    <div className="app" style={{ 
      minHeight: "100vh", background: "#f9f9f9", padding: "20px 16px"
    }}>
      <div style={{ maxWidth: "900px", margin: "0 auto" }}>
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          style={{
            display: "flex", alignItems: "center", gap: "8px",
            background: "none", border: "none", color: "#1976d2",
            fontSize: "1.1rem", cursor: "pointer", marginBottom: "24px", padding: 0,
          }}
        >
          <ArrowLeft size={24} /> Kembali ke Daftar
        </button>

        {/* Main Card */}
        <div style={{
          background: "white", borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)", overflow: "hidden",
          marginBottom: "24px"
        }}>
          {/* Main Image */}
          <div style={{ padding: "16px", textAlign: "center" }}>
            <img
              src={mainImage}
              alt={product.title || "Produk"}
              style={{
                width: "100%", maxWidth: "500px", height: "auto",
                objectFit: "contain", borderRadius: "12px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.1)"
              }}
            />
          </div>

          {/* Product Info */}
          <div style={{ padding: "0 24px 32px" }}>
            <h1 style={{ margin: "0 0 12px", fontSize: "1.8rem", color: "#222" }}>
              {product.title || "Nama produk tidak tersedia"}
            </h1>

            <div style={{ 
              fontSize: "1.6rem", fontWeight: "bold", color: "#e63946", marginBottom: "16px" 
            }}>
              Rp. {product.price 
                ? Number(product.price).toLocaleString("id-ID") 
                : "Harga tidak tersedia"}
            </div>

            <div style={{ fontSize: "1.1rem", color: "#333", marginBottom: "16px" }}>
              <strong>Stok tersedia:</strong> {product.qty ?? "Tidak diketahui"}
            </div>

            <p style={{ 
              color: "#444", lineHeight: 1.7, marginBottom: "24px", whiteSpace: "pre-wrap"
            }}>
              {product.desc || "Tidak ada deskripsi untuk produk ini."}
            </p>

            {/* Seller Contact */}
            <div style={{
              background: "#f8fafc", padding: "20px", borderRadius: "10px",
              margin: "24px 0", border: "1px solid #e2e8f0"
            }}>
              <h3 style={{ margin: "0 0 16px", fontSize: "1.25rem", color: "#1e293b" }}>
                Hubungi Penjual
              </h3>

              {seller ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ fontWeight: 500, color: "#334155" }}>
                    {seller.nama || "Penjual"}
                  </div>

                  {seller.telpon && (
                    <a
                      href={`tel:${seller.telpon}`}
                      style={{
                        display: "flex", alignItems: "center", gap: "10px",
                        color: "#2563eb", textDecoration: "none", fontSize: "1.05rem"
                      }}
                    >
                      <Phone size={20} /> {seller.telpon}
                    </a>
                  )}

                  {seller.wa && (
                    <a
                      href={`https://wa.me/${seller.wa.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "flex", alignItems: "center", gap: "10px",
                        color: "#16a34a", textDecoration: "none", fontSize: "1.05rem"
                      }}
                    >
                      <MessageCircle size={20} /> {seller.wa} (WA)
                    </a>
                  )}

                  {!seller.telpon && !seller.wa && (
                    <div style={{ color: "#64748b" }}>Kontak tidak tersedia</div>
                  )}
                </div>
              ) : (
                <div style={{ color: "#64748b" }}>Memuat info penjual...</div>
              )}
            </div>

            {/* Info Grid */}
            <div style={{
              display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "16px", fontSize: "0.95rem", marginBottom: "24px"
            }}>
              <div><strong>Kategori:</strong> {product.category || "—"}</div>
              <div><strong>Status:</strong> {product.active ? "Aktif" : "Nonaktif"}</div>
              <div><strong>Store ID:</strong> {product.storeid || "—"}</div>
              <div>
                <strong>Link Web:</strong>{" "}
                {product.web ? (
                  <a 
                    href={product.web.startsWith("http") ? product.web : `https://${product.web}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#1976d2", textDecoration: "underline" }}
                  >
                    Buka Link
                  </a>
                ) : "—"}
              </div>
            </div>
          </div>
        </div>

        {/* MAIN FIX: Gambar Tambahan Saat Ini - EXACT sama seperti Update.jsx */}
        {images.length > 0 && (
          <div style={{
            background: "white", 
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)", 
            padding: "24px",
            marginBottom: "32px"
          }}>
            <h3 style={{ margin: "0 0 24px", fontSize: "1.4rem", color: "#222" }}>
              Gambar Tambahan ({images.length})
            </h3>
            <div style={{ 
              display: "flex", 
              flexDirection: "column", 
              gap: "20px" 
            }}>
              {images.map((img, idx) => (
                <div 
                  key={img.id || idx}
                  style={{ 
                    border: "1px solid #ddd", 
                    borderRadius: "8px", 
                    padding: "16px", 
                    background: "#f9f9f9" 
                  }}
                >
                  <img
                    src={`https://api.kamulyaan.id/${img.imagepath}`}
                    alt={`Preview ${idx + 1}`}
                    style={{ 
                      width: "180px", 
                      height: "180px", 
                      objectFit: "cover", 
                      borderRadius: "6px", 
                      marginBottom: "12px" 
                    }}
                  />
                  <p style={{ 
                    fontSize: "0.9rem", 
                    color: "#555", 
                    marginBottom: "8px" 
                  }}>
                    📄 {img.desc || "(kosong)"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Beli Sekarang Button */}
        <div style={{ textAlign: "center", margin: "32px 0" }}>
          <button
            onClick={handleBuyNow}
            style={{
              padding: "14px 48px", background: "#6bbe23", color: "white",
              border: "none", borderRadius: "30px", fontSize: "1.2rem",
              fontWeight: "bold", cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: "12px",
              boxShadow: "0 6px 20px rgba(107,190,35,0.3)",
            }}
          >
            <ShoppingCart size={24} /> Beli Sekarang
          </button>
        </div>
      </div>

      <style>{`
        .app { font-family: system-ui, sans-serif; }
        ::-webkit-scrollbar { height: 8px; }
        ::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: #aaa; }
      `}</style>
    </div>
  );
};

export default Detail;
